import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../../../services/api';

import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import SecondaryButton from '../../../components/Buttons/SecondaryButton';
import { StepTournament } from './steps/StepTournament';
import { StepArenas } from './steps/StepArenas';
import { StepTeams } from './steps/StepTeams';

import {
  NavigationRow,
  PageWrapper,
  Spinner,
  StepperContainer,
} from './styles';
import { ArrowBackIcon } from '../../../assets/svg/SVGComponents';
import { useToast } from '../../../hooks/toast';
import { ISchool, useAuth } from '../../../hooks/auth';

const STEPS = ['Torneio', 'Arenas', 'Equipes'];

/** SVG circle centers (cx) for each of the 3 steps */
const STEP_CX = [31, 217, 403];
const STEP_SPACING = 186;

// Arena from /setup response (template arenas available for the tournament)
export interface ITournamentSetupArena {
  name: string;
  modality: string;
  categories: number[];
}

// Arena belonging to a tournament modality (from POST /tournaments response)
export interface ITournamentArena {
  id: string;
  tournament_modality_id?: string;
  name: string;
  is_active?: boolean;
  created_at?: string;
}

// Modality from the tournament response
export interface ITournamentModality {
  id: string;
  tournament_id?: string;
  modality: string;
  max_rounds?: number;
  time_limit_seconds?: number;
  created_at?: string;
  arenas: ITournamentArena[];
}

export interface ITournamentSetupData {
  id?: string;
  name: string;
  date?: string | null;
  end_date?: string | null;
  submission_deadline?: string | null;
  max_teams_per_school?: string | null;
  status?: string;
  stage?: string;
  location?: string | null;
  school?: ISchool;
  school_id: string;
  season_id: string;
  arenas: ITournamentSetupArena[]; // Available arenas from /setup
  modalities: ITournamentModality[]; // Tournament modalities from POST /tournaments
}

export const MODALITIES: { label: string; slug: TModalitySlug }[] = [
  { label: 'Robô Pintor', slug: 'robo_pintor' },
  { label: 'Seguidor de linha', slug: 'seguidor_de_linha' },
];

export const MODALITY_SLUG_TO_NAME: Record<TModalitySlug, string> = {
  robo_pintor: 'Robô Pintor',
  seguidor_de_linha: 'Seguidor de linha',
};

export const CATEGORY_NUMBER_TO_LABEL: Record<number, string> = {
  1: 'Categoria I: 1º, 2º e 3º ano',
  2: 'Categoria II: 4º e 5º ano',
  3: 'Categoria III: 6º e 7º ano',
  4: 'Categoria IV: 8º e 9º ano',
};

export type TModalitySlug = 'robo_pintor' | 'seguidor_de_linha';

const CreateTournament: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { addToast } = useToast();
  const { onDefaultSchool } = useAuth();

  /*
   * O botão que leva até aqui já fica desabilitado, mas a URL é digitável e o
   * backend recusaria só na última etapa — depois do usuário preencher tudo.
   * Devolver para a lista com o motivo é mais honesto que deixar prosseguir.
   */
  useEffect(() => {
    if (!onDefaultSchool) {
      return;
    }

    addToast({
      type: 'error',
      expression: 'Ops!',
      description:
        'Para criar um torneio, escolha no seu perfil o colégio ao qual você pertence — a Fuzzy Makers é a escola padrão da plataforma.',
    });

    navigate('/tournaments', { replace: true });
  }, [addToast, navigate, onDefaultSchool]);

  const [tournamentData, setTournamentData] = useState<ITournamentSetupData>({
    name: '',
    date: null,
    end_date: null,
    school_id: '',
    season_id: '',
    arenas: [],
    modalities: [],
  });

  useEffect(() => {
    if (onDefaultSchool) {
      return;
    }

    const fetchSetup = async () => {
      try {
        const { data: setup } = await api.get('/tournaments/setup/frl-2026');
        setTournamentData({
          name: setup.name ?? '',
          school: setup.school ?? undefined,
          school_id: setup.school?.id ?? '',
          season_id: setup.season_id ?? '',
          date: setup.date ?? null,
          end_date: setup.end_date ?? null,
          submission_deadline: setup.submission_deadline ?? null,
          max_teams_per_school: setup.max_teams_per_school ?? null,
          stage: setup.stage ?? '',
          status: setup.status ?? '',
          location: setup.location ?? null,
          arenas: setup.arenas ?? [],
          modalities: [],
        });
      } catch {
        addToast({
          type: 'error',
          expression: 'Ops!',
          description:
            'Não foi possível carregar os dados iniciais do torneio. Tente novamente mais tarde.',
        });
        console.error('Erro ao carregar dados iniciais do torneio');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSetup();
  }, [addToast, onDefaultSchool]);

  const handleSave = useCallback(
    (updatedData: Partial<ITournamentSetupData>) => {
      setTournamentData(prev => ({
        ...prev,
        ...updatedData,
      }));

      console.log('Dados do torneio atualizados:', updatedData);
    },
    [],
  );

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(s => s + 1);
    }
    if (currentStep === STEPS.length) {
      navigate(`/tournament/${tournamentData.id}`);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(s => s - 1);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    console.log('Dados do torneio atualizados:', tournamentData);
  }, [tournamentData]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepTournament
            data={tournamentData}
            onCancel={handleBack}
            onSave={tournamentDataEdited => {
              handleSave(tournamentDataEdited);
              handleNext();
            }}
          />
        );
      case 2:
        return (
          <StepArenas
            tournamentData={tournamentData}
            onSave={tournamentDataEdited => {
              handleSave(tournamentDataEdited);
              handleNext();
            }}
            onCancel={handleBack}
          />
        );
      case 3:
        return <StepTeams tournamentData={tournamentData} />;
      default:
        return null;
    }
  };

  return (
    <PageWrapper>
      <StepperContainer>
        <svg
          width="100%"
          viewBox="0 -20 434 118"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Gray background bar */}
          <rect x={25} y={62} width={387} height={10} fill="#D9D9D9" />

          {/* Green progress bar – fills to center of current active step */}
          <rect
            x={25}
            y={62}
            width={STEP_CX[currentStep - 1] - 25}
            height={10}
            fill="#4CAF50"
          />

          {/* Orange V-arrow + vertical line for each step (translated by step index) */}
          {STEP_CX.map((_, idx) => (
            <g key={idx} transform={`translate(${STEP_SPACING * idx}, 0)`}>
              <path
                d="M61.8918 45.2171C54.7988 35.2436 43.2826 29.2396 31.0773 29.1562C18.8719 29.0729 7.24321 34.9346 0.017657 44.8387L3.57721 47.4485C9.96988 38.6878 20.2399 33.5049 31.0502 33.5788C41.8606 33.6526 52.0306 38.9624 58.3031 47.7875L61.8917 45.2234L61.8918 45.2171Z"
                fill="#FF9800"
              />
              <path
                d="M30.6712 8.53304e-05L30.459 31.0776L32.2203 31.0897L32.4326 0.0121148L30.6712 8.53304e-05Z"
                fill="#F39519"
              />
            </g>
          ))}

          {/* Circles + step numbers + labels */}
          {STEP_CX.map((cx, idx) => {
            const stepNum = idx + 1;
            const isCompleted = stepNum < currentStep;
            const isActive = stepNum === currentStep;
            const circleFill = isCompleted || isActive ? '#4CAF50' : '#F2F2F2';
            return (
              <g key={idx}>
                <circle
                  cx={cx}
                  cy={67}
                  r={30.25}
                  fill={circleFill}
                  stroke="#4CAF50"
                  strokeWidth={1.5}
                />
                <text
                  x={cx}
                  y={67}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontFamily="RedHatDisplay-Bold"
                  fontSize={20}
                  fill="#000000"
                >
                  {stepNum}
                </text>
                <text
                  x={cx}
                  y={-5}
                  textAnchor="middle"
                  dominantBaseline="auto"
                  fontFamily="RedHatDisplay-Bold"
                  fontSize={16}
                  fill={isActive || isCompleted ? '#323232' : '#888888'}
                >
                  {STEPS[idx]}
                </text>
              </g>
            );
          })}
        </svg>
      </StepperContainer>

      {isLoading ? <Spinner /> : renderStep()}

      {currentStep > 2 && (
        <NavigationRow>
          <SecondaryButton type="button" onClick={handleBack}>
            <ArrowBackIcon
              width="1.22rem"
              fill="none"
              stroke="#323232"
              strokeWidth={2}
            />
            Voltar
          </SecondaryButton>
          <PrimaryButton type="button" onClick={handleNext}>
            {currentStep === STEPS.length ? 'Finalizar' : 'Próximo'}
            <ArrowBackIcon
              width="1.22rem"
              fill="none"
              stroke="#fff"
              strokeWidth={2}
              style={{ transform: 'rotate(180deg)' }}
            />
          </PrimaryButton>
        </NavigationRow>
      )}
    </PageWrapper>
  );
};

export default CreateTournament;
